import {ExistingProvider, InjectionToken, Provider} from '@angular/core';
import {TokenMap} from './types';
import tokens from './tokens';
import {optionalServiceProviderMap, serviceProviderMap} from './provider-maps';

const objectEntries = <T extends Record<string, unknown>>(object: T) => {
  return Object.entries(object) as [key: keyof T, value: T[keyof T]][];
};

/**
 * Get the list of all required providers for the library,.
 *
 * @param tokenMap Map which binds values and services to their respective token.
 * @returns List of Angular providers which can be used in a module as providers.
 */
export const getConfiguratorProviders = (tokenMap: TokenMap): Provider[] => {
  const providers: Provider[] = [];

  // Process services
  objectEntries(tokenMap.services).forEach(([tokenName, serviceClass]) => {
    const multi = (serviceProviderMap[tokenName] as {multi?: boolean})?.multi ?? false;
    providers.push({
      provide: tokens.services[tokenName],
      useExisting: serviceClass,
      multi,
    } as ExistingProvider);
  });

  // Process values
  objectEntries(tokenMap.values).forEach(([tokenName, value]) => {
    if (value instanceof InjectionToken) {
      providers.push({
        provide: tokens.values[tokenName],
        useFactory: (v: typeof value) => v,
        deps: [value],
      });
    } else {
      providers.push({
        provide: tokens.values[tokenName],
        useValue: value,
      });
    }
  });

  // Process optional services, if present
  if (tokenMap.optionalServices) {
    objectEntries(tokenMap.optionalServices).forEach(([tokenName, serviceClass]) => {
      const multi = (optionalServiceProviderMap[tokenName] as {multi?: boolean})?.multi ?? false;
      providers.push({
        provide: tokens.optionalServices[tokenName],
        useExisting: serviceClass,
        multi,
      } as ExistingProvider);
    });
  }

  return providers;
};
